import { useQuery } from "@tanstack/react-query";
import { adminStorageService } from "@/services/admin-storage-service";
import {
  AggregateStorageStatsDto,
  RegionStorageStatsDto,
  TenantFilesFilters,
  TenantStorageFilesDto,
  TenantStorageFilters,
  TenantStorageUsageListDto,
} from "@/types/admin-storage";

const FIVE_MINUTES = 5 * 60 * 1000;

export const adminStorageKeys = {
  all: ["admin", "storage"] as const,
  regions: () => [...adminStorageKeys.all, "regions"] as const,
  region: (code: string) => [...adminStorageKeys.regions(), code] as const,
  tenants: () => [...adminStorageKeys.all, "tenants"] as const,
  tenantList: (filters: TenantStorageFilters) =>
    [...adminStorageKeys.tenants(), { filters }] as const,
  tenantFiles: (tenantId: string, filters: TenantFilesFilters) =>
    [...adminStorageKeys.tenants(), tenantId, "files", { filters }] as const,
};

export const useStorageRegions = () => {
  return useQuery<AggregateStorageStatsDto>({
    queryKey: adminStorageKeys.regions(),
    queryFn: () => adminStorageService.getStorageRegions(),
    staleTime: FIVE_MINUTES,
  });
};

export const useStorageRegion = (code: string | undefined) => {
  return useQuery<RegionStorageStatsDto>({
    queryKey: adminStorageKeys.region(code || ""),
    queryFn: () => adminStorageService.getStorageRegion(code as string),
    enabled: !!code,
    staleTime: FIVE_MINUTES,
  });
};

export const useStorageTenants = (filters: TenantStorageFilters) => {
  return useQuery<TenantStorageUsageListDto>({
    queryKey: adminStorageKeys.tenantList(filters),
    queryFn: () => adminStorageService.getTenants(filters),
    staleTime: FIVE_MINUTES,
    enabled: !!filters.region,
    placeholderData: (previousData) => previousData,
  });
};

export const useTenantFiles = (
  tenantId: string | undefined,
  filters: TenantFilesFilters,
) => {
  return useQuery<TenantStorageFilesDto>({
    queryKey: adminStorageKeys.tenantFiles(tenantId || "", filters),
    queryFn: () =>
      adminStorageService.getTenantFiles(tenantId as string, filters),
    enabled: !!tenantId,
    staleTime: FIVE_MINUTES,
    placeholderData: (previousData) => previousData,
  });
};
